/** A rectangle to place: a piece's frame, in mm. */
export interface LayoutBox { width: number; height: number }

export interface PackedLayout {
  /** Top-left of each box, in the input order. */
  positions: { x: number; y: number }[];
  width: number;
  height: number;
}

/**
 * Shelf-packs boxes into rows, tallest first, so the arrangement fills an area
 * of the given shape (width / height). Several row widths are tried and the one
 * that shows the boxes largest in that area wins.
 */
export function packBoxes(boxes: LayoutBox[], aspect: number, gap: number): PackedLayout {
  if (boxes.length === 0) return { positions: [], width: 0, height: 0 };
  const widest = Math.max(...boxes.map((box) => box.width));
  const area = boxes.reduce((sum, box) => sum + (box.width + gap) * (box.height + gap), 0);
  const ideal = Math.sqrt(area * aspect);
  const order = boxes.map((box, index) => ({ box, index })).sort((a, b) => b.box.height - a.box.height || a.index - b.index);

  let best: PackedLayout | undefined;
  let bestScale = 0;
  for (let factor = 0.6; factor <= 1.8; factor += 0.05) {
    const layout = shelve(order, Math.max(widest, ideal * factor), gap, boxes.length);
    // How large the layout appears when fitted into a 1 × aspect area.
    const scale = Math.min(aspect / layout.width, 1 / layout.height);
    if (scale > bestScale) { best = layout; bestScale = scale; }
  }
  return best!;
}

function shelve(order: { box: LayoutBox; index: number }[], rowWidth: number, gap: number, count: number): PackedLayout {
  const positions = new Array<{ x: number; y: number }>(count);
  let x = 0, y = 0, rowHeight = 0, width = 0;
  for (const { box, index } of order) {
    if (x > 0 && x + box.width > rowWidth) { x = 0; y += rowHeight + gap; rowHeight = 0; }
    positions[index] = { x, y };
    x += box.width + gap;
    width = Math.max(width, x - gap);
    rowHeight = Math.max(rowHeight, box.height);
  }
  return { positions, width, height: y + rowHeight };
}
